import { useState } from "react";
import { LuCheck, LuSparkles } from "react-icons/lu";
import { Button, cn } from "../ui";
import { SearchField } from "./SearchField";
import { t } from "../../i18n";
import { Kit } from "../../types/order.types";

interface KitListProps<T extends { quantity: number }> {
  kits: Kit<T>[];
  /** Índice na lista completa (não na filtrada): é o que vai salvo no pedido. */
  selected: number;
  onSelect: (index: number) => void;
  itemLabel: (item: T) => string;
  /** Pede a recomendação à IA (só sob demanda: gasta tokens). */
  onRecommend?: () => void;
  recommending?: boolean;
}

// Cartões de pacote para escolher um. A busca por nome só aparece com mais de 2 pacotes.
export function KitList<T extends { quantity: number }>({ kits, selected, onSelect, itemLabel, onRecommend, recommending }: KitListProps<T>) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const visible = kits.map((kit, index) => ({ kit, index })).filter(({ kit }) => kit.name.toLowerCase().includes(q));
  const recommended = kits.find((kit) => kit.recommended);

  return (
    <>
      {recommended ? (
        <p className="flex items-center gap-2 text-label font-semibold text-primary" aria-live="polite">
          <LuSparkles size={18} aria-hidden className="shrink-0" />
          {t("kits.recommendedName", { name: recommended.name })}
        </p>
      ) : (
        onRecommend && (
          <Button variant="outline" size="sm" className="self-start" icon={<LuSparkles size={18} aria-hidden />} loading={recommending} onClick={onRecommend}>
            {t("kits.recommend")}
          </Button>
        )
      )}

      {kits.length > 2 && (
        <SearchField value={query} onChange={setQuery} placeholder={t("kits.searchPlaceholder")} label={t("kits.search")} />
      )}

      <div className="stagger flex flex-col gap-2.5">
        {visible.length === 0 && <p className="py-6 text-center text-caption text-fg-muted">{t("kits.noMatch")}</p>}
        {visible.map(({ kit, index }) => {
          const on = index === selected;
          return (
            <button
              key={kit.id}
              type="button"
              aria-pressed={on}
              onClick={() => onSelect(index)}
              className={cn(
                "relative flex w-full flex-col gap-2.5 rounded-card border-strong p-card text-left shadow-raised transition duration-fast ease-out hover:-translate-y-0.5 focus-visible:outline-none focus-visible:shadow-focus",
                on ? "border-primary bg-primary-selected" : "border-transparent bg-gradient-to-br from-surface from-30% to-primary-soft"
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "absolute right-4 top-4 grid h-7 w-7 place-items-center rounded-full border-2",
                  on ? "animate-scale-in border-primary bg-primary text-on-primary" : "border-primary-line bg-surface text-transparent"
                )}
              >
                <LuCheck size={15} strokeWidth={3} />
              </span>
              {kit.recommended && (
                <span className="self-start rounded-full bg-primary px-2.5 py-1 text-badge font-bold text-on-primary">{t("kits.recommended")}</span>
              )}
              <h3 className="pr-10 text-body font-semibold text-primary">{kit.name}</h3>
              <ul className="flex flex-col gap-1.5">
                {kit.items.map((item, i) => (
                  <li key={i} className="flex justify-between gap-2.5 text-label text-fg-secondary">
                    <span className="min-w-0 break-words">{itemLabel(item)}</span>
                    <span className="shrink-0 font-bold tabular-nums text-primary">x{item.quantity}</span>
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>
    </>
  );
}
